/**
 * Enums do domínio de integrações — espelho de `prisma/schema.prisma`
 * (`origem_evento`, `status_processamento`, `tipo_distribuicao`).
 *
 * RNF-121: os mesmos valores existem no schema Prisma e aqui. Divergência é erro
 * de build, porque a API tipa a resposta com estes tipos e o Prisma tipa a linha
 * com os dele.
 */
import { z } from 'zod'

/**
 * `eventos_integracao.origem` — de qual canal a entrega veio.
 *
 * `LAIS` entrou na 25ª entrega (webhook de qualified lead). É valor próprio e não
 * `API` porque `origem` é a chave da unicidade `(origem, evento_externo_id)`: com a
 * Lais dobrada em `API`, um id de evento dela colidiria com o de qualquer outra
 * integração genérica futura, e a colisão apareceria como "entrega duplicada" — isto
 * é, lead silenciosamente descartado.
 */
export const ORIGENS_EVENTO = ['META_LEADS', 'PORTAL', 'WHATSAPP', 'API', 'LAIS'] as const

export type OrigemEvento = (typeof ORIGENS_EVENTO)[number]

export const STATUS_PROCESSAMENTO = ['PENDENTE', 'PROCESSADO', 'IGNORADO', 'ERRO'] as const

export type StatusProcessamento = (typeof STATUS_PROCESSAMENTO)[number]

/**
 * Estados de onde um evento **pode** ser reprocessado.
 *
 * `IGNORADO` está na lista de propósito: é o estado do evento cujo formulário não
 * estava cadastrado (RN-62). Cadastrar o formulário e mandar reprocessar é
 * justamente o caminho de recuperação previsto na ação `Executar` da TELA-20 — se
 * `IGNORADO` fosse terminal, o payload preservado não serviria para nada.
 *
 * `PROCESSADO` **não** está: reprocessar um evento que já virou lead criaria o
 * segundo lead que o RN-61 existe para impedir.
 */
export const STATUS_REPROCESSAVEIS: ReadonlyArray<StatusProcessamento> = [
  'PENDENTE',
  'ERRO',
  'IGNORADO',
]

/** Estados que a fila varre (índice parcial `eventos_integracao_pendentes_idx`). */
export const STATUS_NA_FILA: ReadonlyArray<StatusProcessamento> = ['PENDENTE', 'ERRO']

/**
 * `formularios_meta.distribuicao` — para quem vai o lead que entra (FL-07).
 *
 * `AUTOMATICA` era o rodízio (round-robin). **C3 recusou** distribuição automática
 * de corretor: o lead novo vai para o responsável da equipe, que faz o
 * pré-atendimento e depois atribui manualmente ao corretor de plantão.
 *
 * O valor continua nesta lista porque continua no enum do banco (remover valor de
 * enum no PostgreSQL não é reversível — ver `migrations/0002`), e porque um
 * formulário lido de uma base antiga precisa ter rótulo. O que **não** pode é ser
 * gravado: use `TIPOS_DISTRIBUICAO_CONFIGURAVEIS`, que é o que os schemas de
 * entrada aceitam.
 */
export const TIPOS_DISTRIBUICAO = [
  'AUTOMATICA',
  'RESPONSAVEL_FIXO',
  'SEM_RESPONSAVEL',
  'RESPONSAVEL_EQUIPE',
] as const

export type TipoDistribuicao = (typeof TIPOS_DISTRIBUICAO)[number]

/**
 * O que a API aceita gravar (C3). `AUTOMATICA` fora: é o rodízio, e não existe
 * atribuição automática de corretor.
 *
 * Dois níveis — este e `TIPOS_DISTRIBUICAO` — em vez de um só, porque "o que pode
 * ser lido" e "o que pode ser escrito" divergem de verdade aqui. Um único enum
 * obrigaria a escolher entre não conseguir exibir uma linha antiga e conseguir
 * gravar um comportamento que o negócio recusou.
 */
export const TIPOS_DISTRIBUICAO_CONFIGURAVEIS = [
  'RESPONSAVEL_EQUIPE',
  'RESPONSAVEL_FIXO',
  'SEM_RESPONSAVEL',
] as const

export type TipoDistribuicaoConfiguravel = (typeof TIPOS_DISTRIBUICAO_CONFIGURAVEIS)[number]

/** C3 — o padrão e o caminho oficial do fluxo. */
export const TIPO_DISTRIBUICAO_PADRAO: TipoDistribuicaoConfiguravel = 'RESPONSAVEL_EQUIPE'

export const zOrigemEvento = z.enum(ORIGENS_EVENTO)
export const zStatusProcessamento = z.enum(STATUS_PROCESSAMENTO)
export const zTipoDistribuicao = z.enum(TIPOS_DISTRIBUICAO)

/**
 * Schema de **entrada**. Recusa `AUTOMATICA` com mensagem que diz o que fazer, em
 * vez de "valor inválido": quem tentar configurar o rodízio precisa saber que a
 * regra mudou, não achar que errou a grafia.
 */
export const zTipoDistribuicaoConfiguravel = z.enum(TIPOS_DISTRIBUICAO_CONFIGURAVEIS, {
  errorMap: (issue) =>
    issue.code === 'invalid_enum_value' && issue.received === 'AUTOMATICA'
      ? {
          message:
            'A distribuição automática por rodízio foi desativada. O lead novo vai para o responsável da equipe, que atribui o corretor de plantão.',
        }
      : { message: 'Forma de distribuição inválida.' },
})

export const ROTULO_STATUS_PROCESSAMENTO: Readonly<Record<StatusProcessamento, string>> = {
  PENDENTE: 'Na fila',
  PROCESSADO: 'Processado',
  IGNORADO: 'Ignorado',
  ERRO: 'Com erro',
}

export const ROTULO_TIPO_DISTRIBUICAO: Readonly<Record<TipoDistribuicao, string>> = {
  RESPONSAVEL_EQUIPE: 'Responsável da equipe (pré-atendimento)',
  RESPONSAVEL_FIXO: 'Responsável fixo',
  SEM_RESPONSAVEL: 'Sem responsável (fila da equipe)',
  // Rótulo de leitura de linha antiga. Não aparece em seletor: as telas montam as
  // opções a partir de `TIPOS_DISTRIBUICAO_CONFIGURAVEIS`.
  AUTOMATICA: 'Distribuição automática (desativada)',
}

/**
 * Desfecho do processamento de um evento. É o vocabulário do log, da resposta de
 * `POST /integracoes/eventos/{id}/reprocessar` e do relatório de sincronização.
 *
 * Existe separado de `StatusProcessamento` porque duas linhas do banco com
 * `PROCESSADO` contam histórias diferentes: uma criou lead, a outra reaproveitou o
 * atendimento que já estava aberto (RN-10). Sem essa distinção, o dashboard de
 * saúde não consegue mostrar quantos leads a integração de fato produziu.
 */
export const DESFECHOS_PROCESSAMENTO = [
  /** Lead novo criado a partir do evento. */
  'LEAD_CRIADO',
  /** RN-10: cliente já tinha atendimento aberto; o payload entrou na timeline dele. */
  'ANEXADO_A_LEAD_EXISTENTE',
  /** RN-62: formulário desconhecido ou inativo. Payload preservado. */
  'IGNORADO_FORMULARIO',
  /** Falha transitória (rede, provedor fora do ar). Volta para a fila. */
  'ERRO_TRANSITORIO',
  /** Falha que retentativa não corrige (payload sem telefone, por exemplo). */
  'ERRO_PERMANENTE',
  /** Evento já processado, ou tomado por outro processo. Nada a fazer. */
  'IGNORADO_JA_TRATADO',
] as const

export type DesfechoProcessamento = (typeof DESFECHOS_PROCESSAMENTO)[number]

export const ROTULO_DESFECHO: Readonly<Record<DesfechoProcessamento, string>> = {
  LEAD_CRIADO: 'Lead criado',
  ANEXADO_A_LEAD_EXISTENTE: 'Anexado ao atendimento existente',
  IGNORADO_FORMULARIO: 'Formulário não cadastrado ou inativo',
  ERRO_TRANSITORIO: 'Falha temporária — na fila para nova tentativa',
  ERRO_PERMANENTE: 'Falha permanente — exige correção manual',
  IGNORADO_JA_TRATADO: 'Evento já tratado',
}

/** Modo do adaptador da Meta. Aparece no dashboard de saúde (RNF-80). */
export const MODOS_PROVEDOR = ['SIMULADO', 'HTTP'] as const

export type ModoProvedor = (typeof MODOS_PROVEDOR)[number]
